// sx-hero-reel (42/A2): the home hero's slides, one list per device (phones below 750 px). In order, a clip advances at
// its end and an image after its time on screen. The next slide loads when the current one starts; at the switch it
// fades in over the current one (600 ms) once its still is decoded, and its clip fades in over that still when it plays,
// so no black frame shows. Plays only in view and in a visible tab; one pause toggle stops it all; reduced motion,
// Save-Data or refused autoplay keep slide 1's still until the toggle is pressed. In the theme editor, a selected
// slide shows and repeats until it is deselected. A rotation or resize across 749 px rebuilds the list.
// Tabs: each progress line is a button; a tap shows that slide (same fade) and the reel carries on from it, playing
// or paused as it was. Clips stream Shopify's HLS playlist where the browser plays HLS itself (Safari, iOS), so a
// phone takes 480p to 1080p by screen and bandwidth; the MP4 plays elsewhere and whenever HLS fails.
// Block scope: the class name stays local, so a second copy of this script cannot throw.
if (!customElements.get('sx-hero-reel')) {
class SxHeroReel extends HTMLElement {
  connectedCallback() {
    if (this.ready) { if (this.io) { this.listen(); this.sync(); } return; }
    this.ready = true;
    this.hero = this.closest('.sx-home-hero');
    this.button = this.hero && this.hero.querySelector('[data-sx-hero-toggle]');
    this.slides = Array.from(this.querySelectorAll('.sx-home-hero__slide'));
    if (!this.hero || !this.slides.length) return;
    this.phone = matchMedia('(max-width: 749px)');
    this.userPaused = false;
    this.inView = true;
    this.started = false;
    this.running = false;
    this.hold = false;
    this.seq = 0;
    this.hls = document.createElement('video').canPlayType('application/vnd.apple.mpegurl') !== '';
    this.slides.forEach((slide) => this.wire(slide));
    this.onTab = (event) => {
      const tab = event.target.closest('[data-sx-hero-tab]');
      if (tab) this.pick(Number(tab.dataset.sxHeroTab));
    };
    this.hero.querySelectorAll('.sx-home-hero__progress').forEach((group) => group.addEventListener('click', this.onTab));
    this.onDevice = () => { this.build(); this.sync(); };
    this.onVisibility = () => this.sync();
    this.onSelect = (event) => this.select(event, true);
    this.onDeselect = (event) => this.select(event, false);
    this.io = new IntersectionObserver((entries) => { this.inView = entries[entries.length - 1].isIntersecting; this.sync(); });
    if (this.button) this.button.addEventListener('click', () => this.toggle());
    this.listen();
    this.build();
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = navigator.connection && navigator.connection.saveData === true;
    if (reduce || saveData) { this.userPaused = true; this.state(); return; }
    this.afterPosterPaint().then(() => { this.started = true; this.sync(); });
  }

  disconnectedCallback() {
    if (!this.io) return;
    this.io.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.phone.removeEventListener('change', this.onDevice);
    this.removeEventListener('shopify:block:select', this.onSelect);
    this.removeEventListener('shopify:block:deselect', this.onDeselect);
    this.halt();
  }

  listen() {
    this.io.observe(this.hero);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.phone.addEventListener('change', this.onDevice);
    this.addEventListener('shopify:block:select', this.onSelect);
    this.addEventListener('shopify:block:deselect', this.onDeselect);
  }

  wire(slide) {
    const video = slide.querySelector('video');
    if (!video) return;
    video.addEventListener('playing', () => {
      if (slide !== this.current || !this.running) { video.pause(); return; }
      slide.classList.add('is-playing');
    });
    video.addEventListener('ended', () => { if (slide === this.current) this.next(); });
    video.addEventListener('error', () => {
      // HLS that fails falls back to the MP4 once; a clip that cannot play keeps its still for its time on screen.
      if ((video.dataset.src || '').includes('.m3u8') && !slide.dataset.hlsFailed) {
        slide.dataset.hlsFailed = 'true';
        delete video.dataset.src;
        this.load(slide);
        if (slide === this.current && this.running) this.play(video);
        return;
      }
      slide.dataset.failed = 'true';
      slide.classList.remove('is-playing');
      if (slide === this.current) this.elapsed = 0;
    });
  }

  // The clip for this device (HLS where the browser plays it, else the MP4), or '' for an image or a clip that failed.
  source(slide) {
    if (!slide || !slide.querySelector('video') || slide.dataset.failed) return '';
    const data = slide.dataset;
    const phone = this.phone.matches;
    const mp4 = phone ? (data.srcM || data.srcD || '') : (data.srcD || '');
    const hls = phone ? (data.srcM ? data.hlsM : data.hlsD) : data.hlsD;
    return this.hls && hls && !data.hlsFailed ? hls : mp4;
  }

  build() {
    this.halt();
    this.hold = false;
    this.pending = false;
    this.switching = false;
    this.seq += 1;
    const key = this.phone.matches ? 'onM' : 'onD';
    this.list = this.slides.filter((slide) => slide.dataset[key] === 'true');
    this.slides.forEach((slide) => {
      slide.classList.remove('is-active', 'is-top', 'is-staged', 'is-playing');
      delete slide.dataset.failed;
      const video = slide.querySelector('video');
      if (video && video.dataset.src) { video.removeAttribute('src'); delete video.dataset.src; video.load(); }
    });
    this.hero.querySelectorAll('.sx-home-hero__bar-line span').forEach((bar) => { bar.style.transform = ''; });
    const group = this.hero.querySelector(`.sx-home-hero__progress--${this.phone.matches ? 'm' : 'd'}`);
    this.tabs = group ? Array.from(group.querySelectorAll('[data-sx-hero-tab]')) : [];
    this.bars = this.tabs.map((tab) => tab.querySelector('.sx-home-hero__bar-line span'));
    this.index = 0;
    this.elapsed = 0;
    this.current = this.list[0];
    clearTimeout(this.fade);
    if (this.current) this.showNow(this.current);
    this.mark();
    this.setAttribute('data-ready', '');
    this.motion = this.list.length > 1 || this.list.some((slide) => this.source(slide));
    this.state();
  }

  sync() {
    const go = this.started && this.inView && !this.userPaused && !document.hidden && this.motion && this.current;
    if (go && !this.running) this.run();
    else if (!go && this.running) this.halt();
  }

  run() {
    this.running = true;
    this.last = performance.now();
    if (this.pending) { this.pending = false; this.next(); } else this.resume();
    cancelAnimationFrame(this.frame);
    if (this.list.length > 1) this.frame = requestAnimationFrame((time) => this.tick(time));
  }

  // Plays the current slide's clip and loads the one after it.
  resume() {
    const slide = this.current;
    if (this.source(slide)) { this.load(slide); this.play(slide.querySelector('video')); }
    if (this.list.length > 1) this.stage(this.list[(this.index + 1) % this.list.length]);
  }

  halt() {
    this.running = false;
    cancelAnimationFrame(this.frame);
    (this.slides || []).forEach((slide) => {
      const video = slide.querySelector('video');
      if (video && !video.paused) video.pause();
    });
  }

  tick(time) {
    if (!this.running) return;
    const slide = this.current;
    let progress = 0;
    if (this.source(slide)) {
      const video = slide.querySelector('video');
      progress = video.duration ? video.currentTime / video.duration : 0;
    } else {
      this.elapsed += Math.max(0, time - this.last);
      progress = this.elapsed / ((parseFloat(slide.dataset.duration) || 6) * 1000);
      if (progress >= 1) this.next();
    }
    this.last = time;
    const bar = this.bars[this.index];
    if (bar) bar.style.transform = `scaleX(${Math.min(progress, 1)})`;
    this.frame = requestAnimationFrame((next) => this.tick(next));
  }

  load(slide) {
    const video = slide.querySelector('video');
    const src = this.source(slide);
    if (!video || !src || video.dataset.src === src) return;
    video.dataset.src = src;
    video.muted = true;
    video.loop = this.list.length === 1;
    video.preload = 'auto';
    video.src = src;
  }

  stage(slide) {
    if (!slide || slide === this.current) return;
    slide.classList.add('is-staged');
    slide.querySelectorAll('img').forEach((img) => { img.loading = 'eager'; });
    // Before play (reduced motion, Save-Data, refused autoplay) a tab shows the still; the clip loads on play.
    if (this.started) this.load(slide);
  }

  // Resolves once the slide's still is decoded (at most 3 s), so the fade never shows an empty frame.
  whenReady(slide) {
    const img = slide.querySelector('img');
    if (!img || !img.decode) return Promise.resolve();
    return Promise.race([img.decode().catch(() => {}), new Promise((done) => setTimeout(done, 3000))]);
  }

  next() {
    if (this.switching || this.list.length < 2) return;
    if (this.hold) { this.restart(); return; }
    this.go((this.index + 1) % this.list.length, false);
  }

  // A tab tap: that slide now, from its start; the reel keeps playing, or stays paused, from there.
  pick(index) {
    if (!this.list || !this.list[index] || index === this.index) return;
    this.hold = false;
    this.pending = false;
    this.go(index, true);
  }

  // Fades to a slide once its still is decoded. The latest request wins (a tap overrides the automatic advance).
  go(index, tapped) {
    const from = this.current;
    const to = this.list[index];
    const seq = ++this.seq;
    this.switching = true;
    this.stage(to);
    this.whenReady(to).then(() => {
      if (seq !== this.seq) return;
      this.switching = false;
      if (this.current !== from) return;
      if (!this.running && !tapped) { this.pending = true; return; }
      this.swap(from, to, index);
    });
  }

  swap(from, to, index) {
    this.bars.forEach((bar, k) => { bar.style.transform = k < index ? 'scaleX(1)' : ''; });
    this.slides.forEach((slide) => { if (slide !== to) slide.classList.remove('is-top'); });
    to.classList.add('is-staged', 'is-top');
    const clip = to.querySelector('video');
    if (clip && clip.readyState && clip.currentTime > 0) { clip.pause(); clip.currentTime = 0; }
    void to.offsetWidth;
    to.classList.add('is-active');
    this.current = to;
    this.index = index;
    this.elapsed = 0;
    this.last = performance.now();
    this.mark();
    if (this.running) this.resume();
    clearTimeout(this.fade);
    this.fade = setTimeout(() => {
      // Every slide still showing under the current one leaves (quick taps can stack more than one).
      this.slides.forEach((slide) => {
        slide.classList.remove('is-top');
        if (slide === this.current || !slide.classList.contains('is-active')) return;
        slide.classList.remove('is-active', 'is-staged', 'is-playing');
        const video = slide.querySelector('video');
        if (video) { video.pause(); if (video.readyState) video.currentTime = 0; }
      });
    }, 650);
  }

  mark() {
    (this.tabs || []).forEach((tab, k) => tab.setAttribute('aria-current', String(k === this.index)));
  }

  restart() {
    this.elapsed = 0;
    if (this.source(this.current)) {
      const video = this.current.querySelector('video');
      video.currentTime = 0;
      this.play(video);
    }
  }

  play(video) {
    const src = video.dataset.src;
    const attempt = video.play();
    if (!attempt) return;
    attempt.catch((error) => {
      // A rejection for a source already replaced (HLS that fell back to MP4) is stale: the error event handled it.
      if (video.dataset.src !== src) return;
      if (error.name === 'NotAllowedError') { this.userPaused = true; this.started = false; this.halt(); this.state(); }
      else if (error.name === 'NotSupportedError') video.dispatchEvent(new Event('error'));
    });
  }

  toggle() {
    this.userPaused = !this.userPaused;
    if (!this.userPaused) this.started = true;
    this.state();
    this.sync();
  }

  state() {
    if (!this.button) return;
    this.button.hidden = !this.motion;
    this.button.setAttribute('aria-pressed', String(this.userPaused));
  }

  // Theme editor: a selected slide shows at once and repeats; deselecting resumes the order.
  select(event, on) {
    const slide = event.target.closest && event.target.closest('.sx-home-hero__slide');
    const index = this.list.indexOf(slide);
    if (index < 0) return;
    this.hold = on;
    if (!on) return;
    this.halt();
    clearTimeout(this.fade);
    this.list.forEach((other) => { if (other !== slide) other.classList.remove('is-active', 'is-top', 'is-staged', 'is-playing'); });
    this.showNow(slide);
    this.current = slide;
    this.index = index;
    this.elapsed = 0;
    this.pending = false;
    this.switching = false;
    this.seq += 1;
    this.bars.forEach((bar, k) => { bar.style.transform = k < index ? 'scaleX(1)' : ''; });
    this.mark();
    this.started = true;
    this.userPaused = false;
    this.state();
    this.sync();
  }

  // Shows a slide at once, without the fade (a rebuild or an editor jump would otherwise fade in from ink).
  showNow(slide) {
    slide.style.transition = 'none';
    slide.classList.add('is-active');
    void slide.offsetWidth;
    slide.style.transition = '';
  }

  afterPosterPaint() {
    const img = this.current && this.current.querySelector('img');
    const loaded = !img || img.complete ? Promise.resolve() : new Promise((done) => {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
    return loaded.then(() => new Promise((done) => requestAnimationFrame(() => requestAnimationFrame(done))));
  }
}
customElements.define('sx-hero-reel', SxHeroReel);
}
